import { Logger } from '@/shared/services/logger';

const log = Logger.createScoped('RtcSession');

// Match mobile's §16 transport ladder:
// direct: host-only, no STUN/TURN; all: TURN UDP + TURN TLS under relay policy; tls: TURN TLS only.
export type RelayTransportMode = 'direct' | 'all' | 'tls';

export interface TurnConfig {
    ip: string;
    username: string;
    credential: string;
}

type TurnInfoLike = {
    turnIp?: string | null;
    turnUsername?: string | null;
    turnCredential?: string | null;
};

type NavigatorWithConnection = Navigator & {
    connection?: {
        type?: string;
        effectiveType?: string;
        downlink?: number;
        rtt?: number;
        saveData?: boolean;
    };
};

export function nextRelayMode(mode: RelayTransportMode): 'all' | 'tls' | null {
    if (mode === 'direct') return 'all';
    if (mode === 'all') return 'tls';
    return null;
}

export function turnConfigFromInfo(turnInfo: TurnInfoLike): TurnConfig {
    if (!turnInfo.turnIp || !turnInfo.turnUsername || !turnInfo.turnCredential) {
        throw new Error('TURN info is incomplete');
    }
    return {
        ip: turnInfo.turnIp,
        username: turnInfo.turnUsername,
        credential: turnInfo.turnCredential
    };
}

export function getCurrentWebNetworkSignature(): string {
    const connection = (navigator as NavigatorWithConnection).connection;
    return JSON.stringify({
        online: navigator.onLine,
        type: connection?.type ?? null,
        effectiveType: connection?.effectiveType ?? null,
        saveData: connection?.saveData ?? null
    });
}

export function buildRtcConfig(mode: RelayTransportMode, turnConfig?: TurnConfig): RTCConfiguration {
    if (mode === 'direct') {
        return {
            iceServers: [],
            iceTransportPolicy: 'all'
        };
    }
    if (!turnConfig) {
        throw new Error(`TURN config is required for ${mode} transport mode`);
    }

    const turnHostname = `ip-${turnConfig.ip.replace(/\./g, '-')}.zynoinfra.net`;
    const tlsServer: RTCIceServer = {
        urls: `turns:${turnHostname}:443`,
        username: turnConfig.username,
        credential: turnConfig.credential
    };
    const iceServers: RTCIceServer[] =
        mode === 'tls'
            ? [tlsServer]
            : [
                  {
                      urls: `turn:${turnConfig.ip}:3478`,
                      username: turnConfig.username,
                      credential: turnConfig.credential
                  },
                  tlsServer
              ];

    return {
        iceServers,
        iceTransportPolicy: 'relay'
    };
}

function sdpContainsCandidateType(sdp: string | undefined, type: 'host' | 'relay'): boolean {
    if (!sdp) return false;
    return sdp.split(/\r?\n/).some(line => line.startsWith('a=candidate:') && line.includes(` typ ${type}`));
}

function candidateHasType(candidate: RTCIceCandidate, type: 'host' | 'relay'): boolean {
    return candidate.type === type || candidate.candidate.includes(` typ ${type} `) || candidate.candidate.endsWith(` typ ${type}`);
}

/**
 * Helper class for managing WebRTC sessions for softphone calls
 */
export class RtcSession {
    private peerConnection: RTCPeerConnection | null = null;
    private localStream: MediaStream | null = null;
    private remoteAudioElement: HTMLAudioElement | null = null;
    private connectionStateCallback: ((state: RTCPeerConnectionState) => void) | null = null;
    private turnConfig?: TurnConfig;
    private relayTransportMode: RelayTransportMode = 'direct';

    /**
     * Initialize a new RTC session with the given configuration
     * @param options - Optional configuration options
     */
    async initialize(options?: {
        turnConfig?: TurnConfig;
        relayTransportMode?: RelayTransportMode;
        microphoneDeviceId?: string;
        speakerDeviceId?: string;
    }): Promise<void> {
        const { turnConfig, relayTransportMode = 'direct', microphoneDeviceId, speakerDeviceId } = options ?? {};
        this.turnConfig = turnConfig;
        this.relayTransportMode = relayTransportMode;

        this.peerConnection = new RTCPeerConnection(buildRtcConfig(this.relayTransportMode, this.turnConfig));

        await this.attachMediaStream(microphoneDeviceId);

        // Create audio element for remote stream
        this.remoteAudioElement = document.createElement('audio');
        this.remoteAudioElement.autoplay = true;
        document.body.appendChild(this.remoteAudioElement);

        // Set speaker device if specified
        if (speakerDeviceId) {
            try {
                await this.remoteAudioElement.setSinkId(speakerDeviceId);
            } catch (err) {
                log.warn('Failed to set speaker device:', err);
            }
        }

        // Handle remote tracks
        this.peerConnection.ontrack = event => {
            if (this.remoteAudioElement && event.streams[0]) {
                this.remoteAudioElement.srcObject = event.streams[0];
            }
        };

        // Log ICE candidates as they're discovered
        this.peerConnection.onicecandidate = event => {
            if (event.candidate) {
                log.info('ICE candidate discovered:', {
                    type: event.candidate.type,
                    protocol: event.candidate.protocol,
                    address: event.candidate.address,
                    port: event.candidate.port,
                    candidate: event.candidate.candidate
                });
            } else {
                log.info('ICE candidate gathering complete');
            }
        };

        // Log ICE connection state changes
        this.peerConnection.oniceconnectionstatechange = () => {
            log.debug('ICE connection state:', this.peerConnection?.iceConnectionState);
        };

        // Log connection state changes
        this.peerConnection.onconnectionstatechange = () => {
            const state = this.peerConnection?.connectionState;
            log.debug('Connection state:', state);
            if (state && this.connectionStateCallback) {
                this.connectionStateCallback(state);
            }
        };
    }

    setRelayTransportMode(mode: RelayTransportMode, turnConfig?: TurnConfig): void {
        if (turnConfig) {
            this.turnConfig = turnConfig;
        }
        if (mode !== 'direct' && !this.turnConfig) {
            throw new Error(`TURN config is required for ${mode} transport mode`);
        }
        if (this.relayTransportMode === mode && !turnConfig) return;

        this.relayTransportMode = mode;
        const config = buildRtcConfig(mode, this.turnConfig);
        if (this.peerConnection) {
            this.peerConnection.setConfiguration(config);
        }
        log.info('Relay transport mode set:', { mode, iceTransportPolicy: config.iceTransportPolicy });
    }

    getRelayTransportMode(): RelayTransportMode {
        return this.relayTransportMode;
    }

    /**
     * Attach user media stream to the peer connection
     * @param microphoneDeviceId - Optional specific microphone device ID to use
     */
    private async attachMediaStream(microphoneDeviceId?: string): Promise<void> {
        if (!this.peerConnection) {
            throw new Error('RTC session not initialized. Call initialize() first.');
        }

        if (this.localStream) {
            log.debug('Media stream already attached');
            return;
        }

        // Get user media (microphone)
        this.localStream = await navigator.mediaDevices.getUserMedia({
            audio: microphoneDeviceId ? { deviceId: { exact: microphoneDeviceId } } : true,
            video: false
        });

        // Remove any existing audio tracks (like silent tracks from pre-init)
        const senders = this.peerConnection.getSenders();
        const newTrack = this.localStream.getAudioTracks()[0];
        if (!newTrack) {
            throw new Error('No audio track in local stream');
        }

        for (const sender of senders) {
            if (sender.track?.kind === 'audio') {
                sender.track.stop();
                await sender.replaceTrack(newTrack);
                log.debug('Replaced silent track with real microphone track');
                return;
            }
        }

        // If no existing sender, add the track
        this.localStream.getTracks().forEach(track => {
            if (this.peerConnection && this.localStream) {
                this.peerConnection.addTrack(track, this.localStream);
            }
        });

        log.debug('Media stream attached to peer connection');
    }

    /**
     * Set the server-issued remote SDP offer. Used for both outbound (the
     * server issues the offer once cPlaceCall is accepted) and inbound calls.
     */
    async setOffer(sdp: string): Promise<void> {
        if (!this.peerConnection) {
            throw new Error('RTC session not initialized');
        }

        const offer = new RTCSessionDescription({
            type: 'offer',
            sdp
        });

        await this.peerConnection.setRemoteDescription(offer);
    }

    async applyOfferAndCreateAnswer(offerSdp: string, turnConfig: TurnConfig, relayTransportMode: RelayTransportMode): Promise<string> {
        this.setRelayTransportMode(relayTransportMode, turnConfig);
        await this.setOffer(offerSdp);
        return this.createAnswer();
    }

    /**
     * Create and return an SDP answer.
     * Returns as soon as the mode-eligible ICE candidate is available:
     * host for direct mode, relay for all/tls.
     */
    async createAnswer(): Promise<string> {
        if (!this.peerConnection) {
            throw new Error('RTC session not initialized');
        }

        const answer = await this.peerConnection.createAnswer();
        await this.peerConnection.setLocalDescription(answer);
        await this.waitForEligibleCandidate();

        return this.peerConnection.localDescription!.sdp;
    }

    /**
     * Wait for the first candidate eligible for the current transport mode.
     * We do not use trickle ICE, so the answer must include a usable candidate
     * before it is submitted to the bridge.
     */
    private waitForEligibleCandidate(): Promise<void> {
        return new Promise((resolve, reject) => {
            const peerConnection = this.peerConnection;
            if (!peerConnection) {
                reject(new Error('Peer connection not initialized'));
                return;
            }
            const eligibleType = this.relayTransportMode === 'direct' ? 'host' : 'relay';

            const hasEligibleCandidate = () => sdpContainsCandidateType(peerConnection.localDescription?.sdp, eligibleType);
            if (hasEligibleCandidate()) {
                resolve();
                return;
            }

            const timeout = setTimeout(() => {
                cleanup();
                reject(new Error(`Timeout waiting for ${eligibleType} candidate`));
            }, 10_000);

            const cleanup = () => {
                clearTimeout(timeout);
                peerConnection.removeEventListener('icecandidate', handleCandidate);
                peerConnection.removeEventListener('icegatheringstatechange', handleGatheringStateChange);
            };

            const complete = () => {
                cleanup();
                resolve();
            };

            const failIfCompleteWithoutEligible = () => {
                if (hasEligibleCandidate()) {
                    complete();
                    return;
                }
                if (peerConnection.iceGatheringState === 'complete') {
                    cleanup();
                    reject(new Error(`ICE gathering completed without ${eligibleType} candidate`));
                }
            };

            const handleCandidate = (event: RTCPeerConnectionIceEvent) => {
                if (event.candidate) {
                    log.debug('ICE candidate:', event.candidate.type, event.candidate.candidate);

                    if (candidateHasType(event.candidate, eligibleType)) {
                        log.debug(`Got ${eligibleType} candidate. Proceeding.`);
                        complete();
                    }
                } else {
                    failIfCompleteWithoutEligible();
                }
            };

            const handleGatheringStateChange = () => {
                failIfCompleteWithoutEligible();
            };

            peerConnection.addEventListener('icecandidate', handleCandidate);
            peerConnection.addEventListener('icegatheringstatechange', handleGatheringStateChange);
            setTimeout(failIfCompleteWithoutEligible, 0);
        });
    }

    /**
     * Set a callback to be called when the connection state changes
     */
    onConnectionStateChange(callback: (state: RTCPeerConnectionState) => void): void {
        this.connectionStateCallback = callback;
    }

    /**
     * Mute the local audio
     */
    mute(): void {
        this.localStream?.getAudioTracks().forEach(track => {
            track.enabled = false;
        });
    }

    /**
     * Unmute the local audio
     */
    unmute(): void {
        this.localStream?.getAudioTracks().forEach(track => {
            track.enabled = true;
        });
    }

    /**
     * Check if the local audio is muted
     */
    isMuted(): boolean {
        return this.localStream?.getAudioTracks().some(track => !track.enabled) ?? false;
    }

    /**
     * Send DTMF tones
     */
    sendDTMF(digit: string): void {
        if (!this.peerConnection) {
            throw new Error('RTC session not initialized');
        }

        const sender = this.peerConnection.getSenders().find(s => s.track?.kind === 'audio');
        if (!sender || !sender.dtmf) {
            throw new Error('DTMF not supported');
        }

        sender.dtmf.insertDTMF(digit, 100, 100);
    }

    /**
     * Set the speaker/output device for remote audio
     */
    async setSpeakerDevice(deviceId: string): Promise<void> {
        if (!this.remoteAudioElement) {
            log.warn('No remote audio element to set speaker device');
            return;
        }
        await this.remoteAudioElement.setSinkId(deviceId);
        log.debug('Speaker device set to:', deviceId);
    }

    /**
     * Set the microphone/input device
     * This replaces the current audio track with one from the new device
     */
    async setMicrophoneDevice(deviceId: string): Promise<void> {
        if (!this.peerConnection) {
            log.warn('No peer connection to set microphone device');
            return;
        }

        // Check if currently muted before replacing track
        const wasMuted = this.isMuted();

        // Get new audio stream from specified device
        const newStream = await navigator.mediaDevices.getUserMedia({
            audio: { deviceId: { exact: deviceId } },
            video: false
        });

        const newTrack = newStream.getAudioTracks()[0];
        if (!newTrack) {
            throw new Error('No audio track from new device');
        }

        // Preserve muted state on new track
        if (wasMuted) {
            newTrack.enabled = false;
        }

        // Find the audio sender and replace the track
        const sender = this.peerConnection.getSenders().find(s => s.track?.kind === 'audio');
        if (sender) {
            // Stop the old track
            this.localStream?.getAudioTracks().forEach(track => track.stop());
            // Replace with new track
            await sender.replaceTrack(newTrack);
            this.localStream = newStream;
            log.debug('Microphone device set to:', deviceId);
        } else {
            log.warn('No audio sender found to replace track');
            newTrack.stop();
        }
    }

    /**
     * Close the RTC session and clean up resources
     */
    close(): void {
        // Stop all local tracks
        this.localStream?.getTracks().forEach(track => track.stop());
        this.localStream = null;

        // Close peer connection
        this.peerConnection?.close();
        this.peerConnection = null;

        // Remove and clean up audio element
        if (this.remoteAudioElement) {
            if (this.remoteAudioElement.parentNode) {
                this.remoteAudioElement.parentNode.removeChild(this.remoteAudioElement);
            }
            this.remoteAudioElement = null;
        }
    }
}
